import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { serieModuloService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const SeriesModulos = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const emptyForm = { codigo: '', descripcion: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await serieModuloService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar módulos de serie.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ codigo: r.codigo || '', descripcion: r.descripcion || '' });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.descripcion) {
      setAlert({ type: 'warning', message: 'Codigo y descripcion son obligatorios.' });
      return;
    }
    const data = { ...form, codigo: form.codigo.toUpperCase() };
    try {
      if (editing) await serieModuloService.update(editing.id_modulo_serie, data);
      else await serieModuloService.create(data);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Módulo de Serie',
      message: `¿Eliminar "${r.codigo} - ${r.descripcion}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await serieModuloService.delete(r.id_modulo_serie); setToast('Módulo eliminado'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 120, style: { fontWeight: 600 } },
    { key: 'descripcion', label: 'Modulo / Descripcion' },
  ];

  return (
    <Layout title="Tipo Doc. Serie" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Módulos de Serie</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_modulo_serie"
        onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay módulos registrados" exportFileName="series_modulos" />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2>{editing ? 'Editar Tipo Doc. Serie' : 'Tipo Doc. Serie'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <label>Codigo *</label>
                  <input name="codigo" value={form.codigo} onChange={handleChange}
                    placeholder="Ej: RQ, OC, GS" maxLength={20} autoFocus
                    style={{ textTransform: 'uppercase' }} />
                  <small style={{ color: '#8899aa', fontSize: 11 }}>
                    Identifica en qué módulo se usarán las series con este código (RQ = Requerimientos, OC = Ordenes de Compra, etc.)
                  </small>
                </div>
                <div className="form-row">
                  <label>Descripcion *</label>
                  <input name="descripcion" value={form.descripcion} onChange={handleChange}
                    placeholder="Ej: Requerimientos" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default SeriesModulos;
